import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogFormGrid, DialogFormSection,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { eduFacultyAPI, employeeAPI, eduSchoolsAPI, eduDepartmentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { GraduationCap, Plus, Pencil, Trash2, Eye, Loader2, Users, BookOpen } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const FACULTY_TYPES = ['permanent', 'visiting', 'adjunct', 'guest', 'contract', 'research'];
const TYPE_LABEL = {
  permanent: 'Permanent', visiting: 'Visiting', adjunct: 'Adjunct',
  guest: 'Guest', contract: 'Contract', research: 'Research',
};

const EMPTY_FORM = {
  employee_id: '', faculty_id: '', school_id: '', department_id: '',
  school: '', department: '', academic_rank: '',
  designation: '', specialization: '', highest_qualification: '', faculty_type: 'permanent',
  teaching_experience_years: 0, industry_experience_years: 0, weekly_teaching_load: 0,
  research_area: '', is_active: true,
};

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.employees || d?.items || d?.results || [];
};

const EducationFacultyManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(false);
  const [faculty, setFaculty] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [detail, setDetail] = useState(null);
  const [schools, setSchools] = useState([]);
  const [departments, setDepartments] = useState([]); // departments of the selected school

  const loadFaculty = async () => {
    setLoading(true);
    try {
      const res = await eduFacultyAPI.list();
      setFaculty(toArray(res));
    } catch (err) {
      // 402 → add-on not active; surface a clear, non-alarming message.
      if (err?.response?.status === 402) {
        setFaculty([]);
      } else {
        Swal.fire('Could not load faculty', err?.response?.data?.detail || 'Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFaculty(); }, []);
  useEffect(() => {
    if (!canManage) return;
    employeeAPI.list().then((res) => setEmployees(toArray(res))).catch(() => setEmployees([]));
    eduSchoolsAPI.list({ status: 'active' }).then((res) => setSchools(toArray(res))).catch(() => setSchools([]));
  }, [canManage]);

  // Cascading dropdown: load only the departments of the selected school.
  const loadDepartments = async (schoolId) => {
    if (!schoolId) { setDepartments([]); return; }
    try {
      const res = await eduDepartmentsAPI.list({ school_id: schoolId, status: 'active' });
      setDepartments(toArray(res));
    } catch { setDepartments([]); }
  };

  const onSchoolChange = (value) => {
    // Changing the school resets the department (it may no longer belong here).
    setForm((prev) => ({ ...prev, school_id: value, department_id: '' }));
    loadDepartments(value);
  };

  const stats = useMemo(() => {
    const total = faculty.length;
    const byType = FACULTY_TYPES.reduce((acc, t) => {
      acc[t] = faculty.filter((f) => f.faculty_type === t).length;
      return acc;
    }, {});
    const load = faculty.reduce((s, f) => s + (Number(f.weekly_teaching_load) || 0), 0);
    return { total, permanent: byType.permanent, visiting: byType.visiting + byType.adjunct + byType.guest, load };
  }, [faculty]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDepartments([]); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...EMPTY_FORM,
      ...row,
      employee_id: String(row.employee_id),
      school_id: row.school_id ? String(row.school_id) : '',
      department_id: row.department_id ? String(row.department_id) : '',
    });
    loadDepartments(row.school_id ? String(row.school_id) : '');
    setDialogOpen(true);
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (!editingId && !form.employee_id) return Swal.fire('Employee required', 'Select the employee this faculty profile extends.', 'warning');
    if (!form.faculty_id?.trim()) return Swal.fire('Faculty ID required', 'Enter a faculty ID (e.g. FAC-0212).', 'warning');
    const payload = {
      ...form,
      school_id: form.school_id ? Number(form.school_id) : null,
      department_id: form.department_id ? Number(form.department_id) : null,
      teaching_experience_years: Number(form.teaching_experience_years) || 0,
      industry_experience_years: Number(form.industry_experience_years) || 0,
      weekly_teaching_load: Number(form.weekly_teaching_load) || 0,
    };
    try {
      if (editingId) {
        const { employee_id, ...rest } = payload; // employee link is immutable
        await eduFacultyAPI.update(editingId, rest);
      } else {
        await eduFacultyAPI.create({ ...payload, employee_id: Number(payload.employee_id) });
      }
      setDialogOpen(false);
      await loadFaculty();
      Swal.fire({ icon: 'success', title: editingId ? 'Faculty updated' : 'Faculty added', timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please review the form and try again.', 'error');
    }
  };

  const openDetail = async (row) => {
    try {
      const res = await eduFacultyAPI.get(row.id);
      setDetail(res.data);
    } catch (err) {
      Swal.fire('Could not open profile', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const remove = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Delete faculty profile?',
      text: `${row.employee_name || row.faculty_id} will lose their faculty profile. The employee record is not affected.`,
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try {
      await eduFacultyAPI.remove(row.id);
      await loadFaculty();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-indigo-600" /> Faculty Management
          </h2>
          <p className="text-sm text-muted-foreground">
            Academic profiles that extend an employee record — rank, type, workload, committees and mentoring.
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> Add Faculty
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Faculty" value={stats.total} icon={Users} />
        <StatCard label="Permanent" value={stats.permanent} icon={GraduationCap} />
        <StatCard label="Visiting / Adjunct / Guest" value={stats.visiting} icon={BookOpen} />
        <StatCard label="Weekly Teaching Load" value={`${stats.load} hrs`} icon={BookOpen} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Faculty Directory</CardTitle>
          <CardDescription>All faculty profiles in this institute.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading faculty…
            </div>
          ) : faculty.length === 0 ? (
            <EmptyState
              icon={GraduationCap}
              title="No faculty profiles yet"
              description={canManage ? 'Add a faculty profile to an existing employee to get started.' : 'No faculty have been added yet.'}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Faculty ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Rank</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Load</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {faculty.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.faculty_id}</TableCell>
                      <TableCell>{row.employee_name || `#${row.employee_id}`}</TableCell>
                      <TableCell>{row.academic_rank || '—'}</TableCell>
                      <TableCell>{row.department || '—'}</TableCell>
                      <TableCell><Badge variant="secondary">{TYPE_LABEL[row.faculty_type] || row.faculty_type}</Badge></TableCell>
                      <TableCell>{row.weekly_teaching_load ? `${row.weekly_teaching_load} hrs` : '—'}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => openDetail(row)} title="View"><Eye className="h-4 w-4" /></Button>
                        {canManage && <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                        {canManage && <Button variant="ghost" size="icon" onClick={() => remove(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Faculty Profile' : 'Add Faculty Profile'}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              {!editingId && (
                <div className="space-y-1">
                  <Label>Employee *</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setField('employee_id', v)}>
                    <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={String(e.id)}>
                          {[e.first_name, e.last_name].filter(Boolean).join(' ')} · {e.employee_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label>Faculty ID *</Label>
                <Input value={form.faculty_id} onChange={(e) => setField('faculty_id', e.target.value)} placeholder="FAC-0212" />
              </div>
              <div className="space-y-1">
                <Label>Faculty Type</Label>
                <Select value={form.faculty_type} onValueChange={(v) => setField('faculty_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FACULTY_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>School</Label>
                <Select value={form.school_id ? String(form.school_id) : ''} onValueChange={onSchoolChange}>
                  <SelectTrigger><SelectValue placeholder={schools.length ? 'Select school' : 'No schools — add one first'} /></SelectTrigger>
                  <SelectContent>
                    {schools.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name} ({s.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Department</Label>
                <Select
                  value={form.department_id ? String(form.department_id) : ''}
                  onValueChange={(v) => setField('department_id', v)}
                  disabled={!form.school_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={!form.school_id ? 'Select a school first' : (departments.length ? 'Select department' : 'No departments available')} />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.name} ({d.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Academic Rank</Label>
                <Input value={form.academic_rank || ''} onChange={(e) => setField('academic_rank', e.target.value)} placeholder="Professor" />
              </div>
              <div className="space-y-1">
                <Label>Designation</Label>
                <Input value={form.designation || ''} onChange={(e) => setField('designation', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Specialization</Label>
                <Input value={form.specialization || ''} onChange={(e) => setField('specialization', e.target.value)} placeholder="Machine Learning" />
              </div>
              <div className="space-y-1">
                <Label>Highest Qualification</Label>
                <Input value={form.highest_qualification || ''} onChange={(e) => setField('highest_qualification', e.target.value)} placeholder="Ph.D." />
              </div>
              <div className="space-y-1">
                <Label>Teaching Experience (yrs)</Label>
                <Input type="number" value={form.teaching_experience_years} onChange={(e) => setField('teaching_experience_years', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Industry Experience (yrs)</Label>
                <Input type="number" value={form.industry_experience_years} onChange={(e) => setField('industry_experience_years', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Weekly Teaching Load (hrs)</Label>
                <Input type="number" value={form.weekly_teaching_load} onChange={(e) => setField('weekly_teaching_load', e.target.value)} />
              </div>
            </DialogFormGrid>
            <div className="space-y-1">
              <Label>Research Area</Label>
              <Input value={form.research_area || ''} onChange={(e) => setField('research_area', e.target.value)} />
            </div>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit}>{editingId ? 'Save changes' : 'Add faculty'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={Boolean(detail)} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.employee_name || detail.faculty_id}
                  <Badge variant="secondary">{TYPE_LABEL[detail.faculty_type] || detail.faculty_type}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="text-sm text-muted-foreground">
                {detail.faculty_id} · {detail.academic_rank || '—'} · {detail.school || '—'}{detail.department ? ` · ${detail.department}` : ''}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm mt-2">
                <div><span className="text-muted-foreground">Specialization: </span>{detail.specialization || '—'}</div>
                <div><span className="text-muted-foreground">Qualification: </span>{detail.highest_qualification || '—'}</div>
                <div><span className="text-muted-foreground">Teaching / Industry: </span>{detail.teaching_experience_years || 0} / {detail.industry_experience_years || 0} yrs</div>
                <div><span className="text-muted-foreground">Weekly load: </span>{detail.weekly_teaching_load || 0} hrs</div>
              </div>
              {detail.research_area && (
                <div className="text-sm mt-1"><span className="text-muted-foreground">Research area: </span>{detail.research_area}</div>
              )}
              <div className="mt-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Committees</div>
                {(detail.committees || []).length === 0
                  ? <div className="text-sm text-muted-foreground">None recorded.</div>
                  : <div className="flex flex-wrap gap-2">{detail.committees.map((c) => (
                      <Badge key={c.id} variant="outline">{c.committee_name}{c.role ? ` · ${c.role}` : ''}</Badge>
                    ))}</div>}
              </div>
              <div className="mt-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Mentor Groups</div>
                {(detail.mentor_groups || []).length === 0
                  ? <div className="text-sm text-muted-foreground">None recorded.</div>
                  : <div className="flex flex-wrap gap-2">{detail.mentor_groups.map((g) => (
                      <Badge key={g.id} variant="outline">{g.group_name}{g.student_count ? ` · ${g.student_count}` : ''}</Badge>
                    ))}</div>}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationFacultyManagement;
